/* eslint-disable sort-exports/sort-exports */
import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../../index";
import type { FieldNeed, CachedNeedsTree } from "../shared/types";

export const GetAppNeedsParams = z.object({
  implementation_id: z
    .string()
    .describe(
      "The app implementation ID with version (e.g., 'GoogleSheetsV2CLIAPI@2.8.0') from get_app_actions",
    ),
  action_key: z
    .string()
    .optional()
    .describe(
      "Optional specific action key to get needs for. If not provided, returns needs for all actions.",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Loading field needs...' or 'Getting form fields...'",
    ),
});

export type GetAppNeedsParamsType = z.infer<typeof GetAppNeedsParams>;

export const getAppNeedsExecute = async (rawArgs: any): Promise<any> => {
  let params: GetAppNeedsParamsType;
  try {
    params = GetAppNeedsParams.parse(rawArgs || {});
  } catch (error) {
    console.error("[GET_APP_NEEDS] Error parsing arguments:", error);
    return {
      $error: {
        message: `Error: Invalid arguments provided for get_app_needs. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
      },
    };
  }

  const { implementation_id, action_key } = params;

  try {
    // Validate implementation_id format
    if (!/^[A-Za-z0-9]+@\d+\.\d+\.\d+$/.test(implementation_id)) {
      return {
        $error: {
          message: `Invalid implementation_id format: ${implementation_id}. Expected format: AppName@version (e.g., GmailCLIAPI@1.5.0)`,
        },
      };
    }

    // Call our backend caching route
    const apiUrl = `/api/canvasui/composer/getAppNeeds?implementation_id=${encodeURIComponent(implementation_id)}`;

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include", // Include cookies for authentication
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = {
          error: `API returned ${response.status}: ${response.statusText}`,
        };
      }

      console.error(`[GET_APP_NEEDS] API error: ${response.status}`, errorData);

      return {
        $error: {
          message: `Failed to fetch app needs: ${errorData.error || response.statusText}`,
        },
      };
    }

    const data = await response.json();

    if (!data.success || !data.needs_tree) {
      return {
        $error: {
          message: `Invalid response format from getAppNeeds API`,
        },
      };
    }

    const needsTree: CachedNeedsTree = data.needs_tree;

    // If action_key is specified, filter to just that action
    if (action_key) {
      const actionNeeds = needsTree[action_key];
      if (!actionNeeds) {
        return {
          $error: {
            message: `Action '${action_key}' not found in app needs. Available actions: ${Object.keys(needsTree).join(", ")}`,
          },
        };
      }

      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(
              {
                implementation_id,
                action_key,
                fields: actionNeeds.fields,
                has_dynamic_fields: actionNeeds.has_dynamic_fields,
                field_count: Object.keys(actionNeeds.fields).length,
                cache_info: data.cache_info || "Retrieved from cache",
              },
              null,
              2,
            ),
          },
        ],
      };
    }

    // Return all actions and their needs
    const actionSummaries = Object.entries(needsTree).map(
      ([key, actionData]) => ({
        action_key: key,
        field_count: Object.keys(actionData.fields).length,
        has_dynamic_fields: actionData.has_dynamic_fields,
        required_fields: Object.values(actionData.fields).filter(
          (field: FieldNeed) => field.required,
        ).length,
        dynamic_fields: Object.values(actionData.fields).filter(
          (field: FieldNeed) => field.dynamic,
        ).length,
      }),
    );

    const totalFields = Object.values(needsTree).reduce(
      (total: number, actionData) =>
        total + Object.keys(actionData.fields).length,
      0,
    );

    return {
      content: [
        {
          type: "text",
          text: JSON.stringify(
            {
              implementation_id,
              app_name: implementation_id.split("@")[0],
              actions_processed:
                data.actions_processed || Object.keys(needsTree).length,
              total_fields: totalFields,
              needs_tree: needsTree,
              action_summaries: actionSummaries,
              cache_info: data.cache_info || "Retrieved from cache",
              summary: {
                actions_with_dynamic_fields: actionSummaries.filter(
                  (a) => a.has_dynamic_fields,
                ).length,
                total_required_fields: actionSummaries.reduce(
                  (sum, a) => sum + a.required_fields,
                  0,
                ),
                total_dynamic_fields: actionSummaries.reduce(
                  (sum, a) => sum + a.dynamic_fields,
                  0,
                ),
              },
            },
            null,
            2,
          ),
        },
      ],
    };
  } catch (error) {
    console.error("[GET_APP_NEEDS] Error in execution:", error);
    return {
      $error: {
        message: `Error fetching app needs: ${error instanceof Error ? error.message : String(error)}`,
      },
    };
  }
};

export const getAppNeedsDescription = `Fetches all field requirements (needs) for a specific Zapier app implementation with caching.

This tool retrieves the complete field structure for all actions in an app, including field types,
requirements, help text, and dynamic field information.

Parameters:
- implementation_id: Full app identifier with version from get_app_actions (e.g., "GmailCLIAPI@1.5.0")
- action_key: Optional specific action to get needs for (if not provided, returns all actions)

Returns:
- Complete needs tree with all actions and their fields
- Field metadata (type, required, help_text, choices, dynamic flags)
- Dynamic field detection (fields that change based on user input)
- Caching information (60-minute cache)
- Summary statistics

The results are cached for 60 minutes to improve performance. Dynamic fields are flagged
for special handling in the UI.

Use this after get_app_actions to understand the specific field requirements for each action.`;

export const getAppNeedsAtom = atom<
  JotaiTool<z.infer<typeof GetAppNeedsParams>>
>(() => ({
  name: "get_app_needs",
  description: getAppNeedsDescription,
  parameters: GetAppNeedsParams,
  execute: atom(
    null, // read function (not used for write-only atom)
    async (_get, _set, { args }) => {
      return getAppNeedsExecute(args);
    },
  ),
}));

getAppNeedsAtom.debugLabel = "getAppNeedsAtom";

/* eslint-enable sort-exports/sort-exports */
